import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../services/api';
import { toast } from 'react-hot-toast';
import PageLayout from '../../components/PageLayout';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'customer' | 'driver';
  kyc_status: 'pending' | 'verified' | 'rejected';
  created_at: string;
  updated_at: string;
  onboardingInvite?: {
    emailDelivery?: {
      success?: boolean;
      error?: string;
    };
  };
}

interface UserFormData {
  email: string;
  password: string;
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/users');
      setUsers(response.data);
    } catch (error: any) {
      toast.error('Failed to load users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiClient.post('/admin/users', { email: formData.email });
      const invite = response.data?.onboardingInvite;
      if (invite?.emailDelivery?.success) {
        toast.success('Admin invite sent successfully');
      } else {
        toast.error(invite?.emailDelivery?.error || 'Admin user was created, but the invite email was not delivered');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    try {
      const updateData = {
        email: formData.email,
        ...(formData.password && { password: formData.password })
      };
      await apiClient.patch(`/admin/users/${editingUser.id}`, updateData);
      toast.success('User updated successfully');
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (currentUser?.id === userId) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingUser(null);
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-on-surface-variant">Loading users...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-on-surface">User Management</h1>
          <p className="text-on-surface-variant mt-1">Invite admin teammates and manage their access.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Admin</span>
        </button>
      </div>

      {/* Filters */}
      <div className="admin-subpanel rounded-card p-4">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-on-surface-variant" />
            <input
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
            />
          </div>

          <div className="admin-subpanel rounded-xl p-4 text-sm text-on-surface-variant">
            New users are created as admins and receive an email to set their password before first sign-in.
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="admin-subpanel rounded-card p-4">
          <div className="text-sm text-on-surface-variant">Total Users</div>
          <div className="text-2xl font-bold text-on-surface">{users.length}</div>
        </div>
        <div className="admin-subpanel rounded-card p-4">
          <div className="text-sm text-on-surface-variant">Admins</div>
          <div className="text-2xl font-bold text-primary">
            {users.filter(u => u.role === 'admin').length}
          </div>
        </div>
      </div>

      {/* User Table */}
      <div className="admin-table-card">
        <table className="admin-table-grid min-w-full">
          <thead className="admin-table-head">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                Access
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                Created At
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="admin-table-body">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="admin-table-row">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="admin-avatar-tile flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-on-surface-variant" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-on-surface">{user.email}</div>
                      <div className="text-sm text-on-surface-variant">ID: {user.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="admin-soft-pill px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary/15 text-on-surface">
                    admin
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => openEditModal(user)}
                    className="mr-4 text-primary hover:text-on-surface"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id, user.email)}
                    className="text-error hover:text-on-error-container"
                    disabled={currentUser?.id === user.id}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-on-surface-variant" />
            <h3 className="mt-2 text-sm font-medium text-on-surface">No users found</h3>
            <p className="mt-1 text-sm text-on-surface-variant">
              {searchTerm
                ? 'Try adjusting your filters'
                : 'Get started by creating a new user'}
            </p>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => {
            setShowModal(false);
            setEditingUser(null);
            resetForm();
          }}
        >
          <div
            className="bg-surface-container-lowest rounded-card shadow-float max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-on-surface mb-4">
              {editingUser ? 'Edit User' : 'Invite Admin'}
            </h2>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field mt-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-on-surface-variant">
                    {editingUser ? 'Password (leave blank to keep current)' : 'Invite flow'}
                  </label>
                  {editingUser ? (
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-field mt-1"
                    />
                  ) : (
                    <div className="admin-subpanel mt-1 rounded-xl p-3 text-sm text-on-surface-variant">
                      This user will be created as an admin and sent an email to choose their own password.
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                    className="px-4 py-2 border border-ghost rounded-md text-sm font-medium text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover"
                >
                  {editingUser ? 'Update User' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
